/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { AnonymousSessionInfo, FixedMessenger, ManualSessionInfo, MessengerSessionRef } from '@sendbird/ai-agent-messenger-react';
import { SessionHandler } from '@sendbird/chat';
import { AgentTokenData, ResponseEntity } from './+model/delight-support.model';
import { DELIGHT_SUPPORT_CONFIG, DELIGHT_DISPLAY_NAME } from './delight-support.tokens';
import { DelightSupportConfig } from './+model/delight-support.model';

@Injectable({
  providedIn: 'root'
})
export class DelightSupportService implements OnDestroy {
  private reactRoot: Root | null = null;
  private messengerRef = React.createRef<MessengerSessionRef>();
  private isOpen: boolean = false;
  private sessionInfo: ManualSessionInfo | AnonymousSessionInfo | null = null;
  private agentToken: string = '';
  // Resolved per user by the token endpoint; falls back to the config default.
  private agentId: string = '';
  hasMessengerInstance: boolean = false;
  private tokenRefreshTimeoutId: any = null;
  private tokenExpiresAt: number = 0;
  private focusabilityObserver: MutationObserver | null = null;

  constructor(
    private http: HttpClient,
    @Inject(DELIGHT_SUPPORT_CONFIG) private config: DelightSupportConfig,
    @Inject(DELIGHT_DISPLAY_NAME) private getDisplayName: () => string
  ) {}

  fetchSupportToken(): Observable<AgentTokenData> {
    return this.http
      .post<ResponseEntity<AgentTokenData>>(this.config.tokenEndpoint, {}, {
        withCredentials: true,
      })
      .pipe(map(response => response.data));
  }

  async launchChat(): Promise<boolean> {
    console.debug('Launching chat support...');
    try {
      if (this.reactRoot) {
        console.debug('Opening existing messenger instance');
        this.isOpen = true;
        this.renderMessenger();
        this.focusWhenReady();
        this.armTokenRefreshTimer();
        return true;
      }
      await this.initializeMessenger();

      return true;
    } catch (error) {
      console.error('Failed to load chat support:', error);
      return false;
    }
  }

  openMessenger(): void {
    if (this.reactRoot) {
      this.isOpen = true;
      this.renderMessenger();
      this.focusWhenReady();
      this.armTokenRefreshTimer();
    } else {
      console.error('Messenger instance is not initialized');
    }
  }

  closeMessenger(): void {
    if (this.reactRoot) {
      console.debug('Closing messenger interface');
      this.isOpen = false;
      this.renderMessenger();
      this.clearTokenRefreshTimeout();
    } else {
      console.error('Messenger instance is not initialized');
    }
  }

  private async refreshAgentToken(): Promise<void> {
    if (!this.reactRoot) {
      console.debug('No active messenger instance, skipping token refresh');
      return;
    }

    try {
      console.debug(`[${this.ts()}] Refreshing agent token...`);
      const supportTokenObject = await firstValueFrom(this.fetchSupportToken());
      console.debug(`[${this.ts()}] New agent token received`);

      await this.messengerRef.current?.patchContext({
        agentToken: supportTokenObject.agentToken
      });
      this.agentToken = supportTokenObject.agentToken;
      // Keep the rendered agent matching the one the refreshed token was minted for.
      this.agentId = supportTokenObject.agentId || this.config.agentId || '';
      this.renderMessenger();
      console.debug(`[${this.ts()}] Messenger context updated with new token`);

      const contextObject = await this.messengerRef.current?.getContextObject();
      console.debug(`[${this.ts()}] Current messenger context:`, contextObject);

      this.scheduleTokenRefresh(supportTokenObject.tokenDuration);
    } catch (error) {
      console.error('Failed to refresh agent token:', error);
      this.scheduleTokenRefresh(60);
    }
  }

  private scheduleTokenRefresh(tokenDuration: number): void {
    this.tokenExpiresAt = Date.now() + tokenDuration * 1000;
    this.armTokenRefreshTimer();
  }

  private armTokenRefreshTimer(): void {
    this.clearTokenRefreshTimeout();
    const scheduledMs = Math.max(0, this.tokenExpiresAt - Date.now() - 29_000);
    const fireAt = new Date(Date.now() + scheduledMs);
    console.debug(`[${this.ts()}] Scheduling token refresh at ${this.ts(fireAt)} (in ${Math.round(scheduledMs / 1000)}s)`);

    this.tokenRefreshTimeoutId = setTimeout(() => {
      this.refreshAgentToken();
    }, scheduledMs);
  }

  private ts(date: Date = new Date()): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }

  private clearTokenRefreshTimeout(): void {
    if (this.tokenRefreshTimeoutId) {
      clearTimeout(this.tokenRefreshTimeoutId);
      this.tokenRefreshTimeoutId = null;
    }
  }

  private async initializeMessenger(): Promise<void> {
    const supportTokenObject = await firstValueFrom(this.fetchSupportToken());
    console.debug(`[${this.ts()}] New agent token received`);
    console.debug(`[${this.ts()}] Initializing messenger with userId=${supportTokenObject.userId}, sessionToken=${!!supportTokenObject.sessionToken}, agentToken=${!!supportTokenObject.agentToken}`);

    if (supportTokenObject.sessionToken && supportTokenObject.userId) {
      const sessionHandler = new SessionHandler({
        onSessionTokenRequired: async (resolve, reject) => {
          console.debug(`[${this.ts()}] Session token required, refreshing session...`);
          try {
            const tokenData = await firstValueFrom(this.fetchSupportToken());
            if (!tokenData.sessionToken) {
              console.error(`[${this.ts()}] No session token returned from support token endpoint`);
              reject(new Error('No session token returned from support token endpoint'));
              return;
            }
            this.agentToken = tokenData.agentToken;
            // The BFF re-resolves the agent on every token call, so keep the two in step.
            this.agentId = tokenData.agentId || this.config.agentId || '';
            resolve(tokenData.sessionToken);
          } catch (error) {
            console.error(`[${this.ts()}] Session handler error:`, error);
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        },
        onSessionRefreshed: async () => {
          console.debug(`[${this.ts()}] Session refreshed, updating agent context with current token...`);
          await this.messengerRef.current?.patchContext({
            agentToken: this.agentToken
          });
          // aiAgentId is a render prop, so the patch alone would leave the messenger on the
          // previous agent. Render after the patch resolves, never before.
          this.renderMessenger();
        },
        onSessionClosed: () => {
          console.debug(`[${this.ts()}] Session closed, reinitializing messenger...`);
          this.destroyMessengerInstance();
          this.initializeMessenger().catch(error => {
            console.error('Failed to reinitialize messenger after session close:', error);
          });
        }
      });

      this.sessionInfo = new ManualSessionInfo({
        userId: supportTokenObject.userId.toString(),
        authToken: supportTokenObject.sessionToken,
        sessionHandler,
      });
    } else {
      console.warn('No session token returned; using anonymous session for Delight Support.');
      this.sessionInfo = new AnonymousSessionInfo();
    }
    this.agentToken = supportTokenObject.agentToken;
    this.agentId = supportTokenObject.agentId || this.config.agentId || '';

    const container = document.createElement('div');
    container.setAttribute('data-sb-agent-messenger', '');
    document.body.appendChild(container);
    this.reactRoot = createRoot(container);
    this.hasMessengerInstance = true;
    this.isOpen = true;

    this.patchHiddenElementFocusability(container);
    this.renderMessenger();
    this.focusWhenReady();
    this.scheduleTokenRefresh(supportTokenObject.tokenDuration);
  }

  private renderMessenger(): void {
    if (!this.reactRoot || !this.sessionInfo) {
      return;
    }

    const setOpened = (value: boolean) => {
      this.isOpen = value;
      this.renderMessenger();
    };

    this.reactRoot.render(
      React.createElement(
        FixedMessenger,
        {
          appId: this.config.appId,
          aiAgentId: this.agentId,
          userSessionInfo: this.sessionInfo,
          language: 'en-US',
          context: {
            userName: this.getDisplayName(),
            agentToken: this.agentToken,
          },
          state: {
            opened: this.isOpen,
            setOpened,
          },
          ref: this.messengerRef,
          theme: {
            palette: {
              primary: {
                extraDark: '#004A78',
                dark: '#025B90',
                main: '#0272B4',
                light: '#489DCF',
                extraLight: '#CFE9FB',
              },
            },
          },
        },
        React.createElement(FixedMessenger.Style, {
          position: 'end-bottom',
          margin: { end: 50 },
        }),
      )
    );
  }

  // Focus management when the widget is opened programmatically. The widget's
  // internal C() focus handler only fires on launcher-button clicks, not on
  // state.setOpened calls, so we handle it here.
  //
  // Target priority (mutually exclusive based on which view is active):
  //   1. [data-message-composer] textarea — conversation view, input ready
  //   2. [aria-label="Start a conversation"] — conversation list view
  //
  // The MutationObserver fallback handles two timing hazards:
  //   • Initial render: React hasn't committed content yet when focusWhenReady() fires.
  //   • Disabled textarea: AI is still responding when the widget opens; the
  //     textarea gains its enabled state via a DOM mutation, not a new render.
  private focusWhenReady(): void {
    const selectors = [
      '[data-sb-agent-messenger] [data-message-composer] textarea:not(:disabled)',
      '[data-sb-agent-messenger] [aria-label="Start a conversation"]',
    ];

    let observerAttached = false;

    const attachObserver = () => {
      if (observerAttached) return;
      observerAttached = true;
      const observer = new MutationObserver(() => {
        const hasUnblocked = selectors.some(s => {
          const el = document.querySelector<HTMLElement>(s);
          return el !== null && !el.closest('[inert]');
        });
        if (hasUnblocked) {
          observer.disconnect();
          observerAttached = false;
          requestAnimationFrame(tryFocus);
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    };

    const tryFocus = () => {
      for (const selector of selectors) {
        const el = document.querySelector<HTMLElement>(selector);
        if (el && !el.closest('[inert]') && !el.hasAttribute('disabled')) {
          el.focus();
          return;
        }
      }
      // A candidate exists but is blocked (inert ancestor or disabled) — retry next frame.
      // No candidates at all means the widget hasn't rendered yet — wait via observer.
      const hasUnblocked = selectors.some(s => {
        const el = document.querySelector<HTMLElement>(s);
        return el !== null && !el.closest('[inert]');
      });
      if (hasUnblocked) {
        requestAnimationFrame(tryFocus);
      } else {
        attachObserver();
      }
    };

    tryFocus();
  }

  // The widget switches between conversation-list and conversation-view panels by
  // toggling z-index (999 = visible, 1 = hidden). Both panels stay in the DOM, so
  // without intervention the hidden one remains in the tab order. A MutationObserver
  // watches for style/class changes and applies inert to whichever panel is behind.
  //
  // Also calls focusWhenReady() whenever a panel becomes active (z-index goes to 999)
  // so that navigating between views moves focus to the appropriate input, mirroring
  // what focusWhenReady() does on widget open. The initial scan is skipped because
  // initializeMessenger() already calls focusWhenReady() at launch time.
  private patchHiddenElementFocusability(container: HTMLElement): void {
    const panelZIndexes = new Map<HTMLElement, string>();
    let isInitialScan = true;

    const update = () => {
      const panels = container.querySelectorAll<HTMLElement>('#sb-agent-window > * > *');
      panels.forEach((panel) => {
        const zIndex = getComputedStyle(panel).zIndex;
        if (zIndex !== '1' && zIndex !== '999') return;

        const prev = panelZIndexes.get(panel);
        panelZIndexes.set(panel, zIndex);
        panel.inert = zIndex === '1';

        if (!isInitialScan && zIndex === '999' && prev !== '999') {
          this.focusWhenReady();
        }
      });
      isInitialScan = false;
    };

    this.focusabilityObserver = new MutationObserver(() => {
      requestAnimationFrame(update);
    });
    this.focusabilityObserver.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  private destroyMessengerInstance(): void {
    this.clearTokenRefreshTimeout();
    this.focusabilityObserver?.disconnect();
    this.focusabilityObserver = null;
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
    document.querySelector('[data-sb-agent-messenger]')?.remove();
    this.hasMessengerInstance = false;
    this.sessionInfo = null;
    this.agentToken = '';
    this.messengerRef = React.createRef<MessengerSessionRef>();
  }

  ngOnDestroy(): void {
    this.destroyMessengerInstance();
  }
}
